import React from 'react';
import { useIntl } from 'react-intl';
import {
  Button,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { CenteredFlexBox } from '../../FlexBox';
import useTranslate from '../../../Hooks/useTranslate';
import useLayoutEdit from './useLayoutEdit';
import LayoutPicker from './LayoutPicker';
import DeleteLayoutButton from './DeleteLayoutButton';
import { SURFACE_LABELS } from './surfaceLabels';

/**
 * The navbar while a layout is being edited: which layout it is, and what may be done to it.
 *
 * The page below is the layout itself, so what would be a dialog's title bar is the application's,
 * and nothing covers what is being arranged.
 *
 * @returns {React.JSX.Element}
 */
const LayoutEditBar = function LayoutEditBar() {
  const intl = useIntl();
  const translate = useTranslate();
  const { session, selectLayout, setName, discard, save, remove, close } =
    useLayoutEdit();
  // Below xl the bar beside the palette cannot hold every label at full length: the title and the
  // two fields give up width first, and the discard button its second word.
  const compact = useMediaQuery((theme) => theme.breakpoints.down('xl'));

  if (!session) return null;

  const title = `${intl.messages.EDIT_LAYOUT} — ${translate(
    SURFACE_LABELS[session.surface] ?? session.surface
  )}`;

  return (
    // On a surface of its own, like everything else the navbar carries: the bar takes the place of
    // the papers the row is otherwise made of, and sitting bare against the page read as unfinished.
    <Paper
      sx={{
        flex: 1,
        minWidth: 0,
        borderRadius: '18px',
        padding: '8px 20px',
      }}
    >
      <CenteredFlexBox gap="12px">
        <Typography
          variant="h6"
          sx={{ flex: '0 1 auto', minWidth: 0 }}
          title={title}
          noWrap
        >
          {title}
        </Typography>
        <LayoutPicker
          layouts={session.layouts}
          value={session.layoutId}
          name={session.name}
          sx={{ flex: '0 1 220px', minWidth: '140px' }}
          onSelect={selectLayout}
          onNameChange={setName}
        />
        {/* Never shrunk: these are the ways out of the editor, and a squeezed row had pushed "Salva"
            and the close button past the edge of the screen. */}
        <CenteredFlexBox
          gap="8px"
          sx={{ flex: '1 0 auto', justifyContent: 'flex-end' }}
        >
          <Tooltip arrow title={compact ? intl.messages.DISCARD_CHANGES : ''}>
            <Button
              id="reset-layout-btn"
              color="inherit"
              onClick={discard}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {compact ? intl.messages.CANCEL : intl.messages.DISCARD_CHANGES}
            </Button>
          </Tooltip>
          <DeleteLayoutButton
            disabled={!session.layoutId}
            name={session.name}
            onDelete={remove}
          />
          <Button
            id="save-layout-btn"
            variant="contained"
            disabled={session.regions.length === 0}
            onClick={save}
          >
            {intl.messages.SAVE}
          </Button>
          <Tooltip arrow title={intl.messages.CLOSE}>
            <IconButton id="close-layout-btn" onClick={close}>
              <CloseRoundedIcon />
            </IconButton>
          </Tooltip>
        </CenteredFlexBox>
      </CenteredFlexBox>
    </Paper>
  );
};

export default LayoutEditBar;
